/* eslint-disable @typescript-eslint/no-use-before-define */
import { all, call, fork, put, takeEvery, select } from "redux-saga/effects";
import { NotificationManager } from "@kernel-helpers/react-notifications";
import {
  acceptUserIntoCompanyFirebaseHelper,
  getRequestingUsers,
  createNewGroupWithUsers,
  deleteGroup,
  addUserToGroup,
  removeUserFromGroup,
  denyUserRequest,
  getCompanysGroups,
  deleteUser,
} from "@kernel-helpers/DatabaseHelpers";
import {
  initializeVirgil,
  isVirgilInitialized,
  acceptUserIntoCompanyVirgilHelper,
  joinGroup,
  restorePrivateKey,
} from "@tumeke/tumekejs/lib/auth";
import {
  ACCEPT_USER_INTO_COMPANY_REQUEST,
  INITIALIZE_REQUEST_LIST_REQUEST,
  INITIALIZE_GROUPS_LIST_REQUEST,
  CREATE_NEW_GROUP_REQUEST,
  DELETE_GROUP_REQUEST,
  MOVE_USER_TO_GROUP_REQUEST,
  ADD_USER_TO_GROUP_REQUEST,
  DENY_USER_REQUEST,
  ACCEPT_AND_ADD_TO_GROUP,
  DELETE_USER_REQUEST,
} from "@kernel-store/actions";

import { ReduxState } from "@kernel-store/reducers";
import {
  acceptUserIntoCompanySuccess,
  initializeRequestListSuccess,
  initializeGroupsListSuccess,
  createNewGroupSuccess,
  deleteGroupSuccess,
  moveUserToGroupSuccess,
  addUserToGroupSuccess,
  denyUserSuccess,
  acceptUserIntoCompanyRequest,
  addUserToGroupRequest,
  createNewGroupRequest,
  deleteUserSuccess,
  deleteUserError,
} from "./actions";
import { loginUserSuccess } from "../auth/actions";

export function* watchAcceptIntoCompany() {
  yield takeEvery(
    ACCEPT_USER_INTO_COMPANY_REQUEST,
    acceptUserIntoCompanySagaHelper,
  );
}

export function* watchInitializeRequestList() {
  yield takeEvery(INITIALIZE_REQUEST_LIST_REQUEST, initializeRequestListHelper);
}

export function* watchCreateNewGroup() {
  yield takeEvery(CREATE_NEW_GROUP_REQUEST, createNewGroupSagaHelper);
}

export function* watchDeleteGroup() {
  yield takeEvery(DELETE_GROUP_REQUEST, deleteGroupSagaHelper);
}

export function* watchInitializeGroupsList() {
  yield takeEvery(INITIALIZE_GROUPS_LIST_REQUEST, initializeGroupsListHelper);
}

export function* watchMoveUserToGroupRequest() {
  yield takeEvery(MOVE_USER_TO_GROUP_REQUEST, moveUserToGroupHelper);
}

export function* watchAddUserToGroupRequest() {
  yield takeEvery(ADD_USER_TO_GROUP_REQUEST, addUserToGroupHelper);
}

export function* watchDenyUserRequest() {
  yield takeEvery(DENY_USER_REQUEST, denyUserHelper);
}

export function* watchAcceptAndAdd() {
  yield takeEvery(ACCEPT_AND_ADD_TO_GROUP, acceptAndAddSaga);
}

export function* watchDeleteUserRequest() {
  yield takeEvery(DELETE_USER_REQUEST, deleteUserSaga);
}

const handleProfileError = (e: any) => {
  if (e.response) {
    // TODO Change this
    NotificationManager.error(
      "Error",
      `${e.response.data.message} Please try again`,
      3000,
      null,
      null,
      "",
    );
    return;
  }
  NotificationManager.error(
    "Error",
    "Unknown server error. Please try again",
    3000,
    null,
    null,
    "",
  );
};

function* acceptAndAddSaga({ payload }: any) {
  const { user, groupName, isNewGroup } = payload;
  yield put(acceptUserIntoCompanyRequest(user));
  if (isNewGroup) {
    yield put(createNewGroupRequest([user], groupName));
  } else {
    yield put(addUserToGroupRequest(user, groupName));
  }
}

function* acceptUserIntoCompanySagaHelper({ payload }: any) {
  const state: ReduxState = yield select();
  const { user, company } = state.authUser;
  if (!user.isSSO && !company.virgil_off) {
    if (!isVirgilInitialized()) {
      console.log("not initialized");
      yield call(initializeVirgil, user.virgil_id);
      yield call(restorePrivateKey, "");
      yield call(joinGroup, user, state.authUser.company);
    }

    console.log(`VIRGIL ID: ${payload.user.virgil_id}`);
    try {
      yield call(acceptUserIntoCompanyVirgilHelper, payload.user.virgil_id);
    } catch (e) {
      NotificationManager.warning(
        "Error",
        "Error in verifying ID. Please refresh page and try again.",
        3000,
        null,
        null,
        "",
      );
      return;
    }
  }
  try {
    yield call(acceptUserIntoCompanyFirebaseHelper, payload.user.id);
  } catch (e) {
    handleProfileError(e);
    return;
  }

  yield put(acceptUserIntoCompanySuccess(payload.user.id));
}

function* initializeRequestListHelper() {
  let requestList: any = null;
  try {
    requestList = (yield call(getRequestingUsers)) as unknown as any;
  } catch (e) {
    handleProfileError(e);
    return;
  }

  yield put(initializeRequestListSuccess(requestList));
}

function* createNewGroupSagaHelper({ payload }: any) {
  let userGroups: any = null;
  console.log(`NAME1: ${payload.groupName}`);
  try {
    yield call(createNewGroupWithUsers, payload.users, payload.groupName);
    userGroups = (yield call(getCompanysGroups)) as unknown as any;
  } catch (e) {
    handleProfileError(e);
    return;
  }

  yield put(createNewGroupSuccess(userGroups.groups));
}

function* deleteGroupSagaHelper({ payload }: any) {
  try {
    yield call(deleteGroup, payload.groupId);
  } catch (e) {
    handleProfileError(e);
    return;
  }
  yield put(deleteGroupSuccess(payload.groupId));
}

function* initializeGroupsListHelper() {
  const state = (yield select()) as unknown as any;
  let data = null;
  try {
    data = (yield call(getCompanysGroups)) as unknown as any;
  } catch (e) {
    handleProfileError(e);
    return;
  }

  console.log(`GROUPS: ${data.groups}`);
  console.log(`COMPANY: ${data.company}`);
  const currentCompany = state.authUser.company;

  yield put(
    loginUserSuccess(state.authUser.user, {
      ...data.company,
      aes_key: currentCompany.aes_key,
    }),
  );
  yield put(initializeGroupsListSuccess(data.groups));
}

function* moveUserToGroupHelper({ payload }: any) {
  try {
    yield call(addUserToGroup, payload.userId, payload.toGroup);
    yield call(removeUserFromGroup, payload.userId, payload.fromGroup);
  } catch (e) {
    handleProfileError(e);
    return;
  }

  yield put(
    moveUserToGroupSuccess(payload.userId, payload.toGroup, payload.fromGroup),
  );
}

function* addUserToGroupHelper({ payload }: any) {
  let userGroups = null;
  try {
    yield call(addUserToGroup, payload.user.id, payload.groupId);
    userGroups = (yield call(getCompanysGroups)) as unknown as any;
  } catch (e) {
    handleProfileError(e);
    return;
  }

  yield put(addUserToGroupSuccess(userGroups.groups));
}

function* denyUserHelper({ payload }: any) {
  yield call(denyUserRequest, payload.userId);
  yield put(denyUserSuccess(payload.userId));
}

function* deleteUserSaga({ payload }: any) {
  const { fromGroup, userId } = payload;
  try {
    (yield call(deleteUser, userId)) as unknown as any;
    yield put(deleteUserSuccess(fromGroup, userId));
  } catch (e: any) {
    yield put(deleteUserError(e?.message || "Error deleting user"));
    console.log("DELETE USER ERROR", e);
  }
}

export default function* rootSaga() {
  yield all([
    fork(watchAcceptIntoCompany),
    fork(watchInitializeRequestList),
    fork(watchCreateNewGroup),
    fork(watchDeleteGroup),
    fork(watchInitializeGroupsList),
    fork(watchMoveUserToGroupRequest),
    fork(watchAddUserToGroupRequest),
    fork(watchDenyUserRequest),
    fork(watchAcceptAndAdd),
    fork(watchDeleteUserRequest),
  ]);
}
